import {
	DOMOutputSpec,
	Node as ProseMirrorNode,
	Schema,
	DOMSerializer,
	DOMParser,
	Node,
} from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import { history } from "prosemirror-history";
import { DefaultKeymap } from "./Keybindings";

const pDOM: DOMOutputSpec = ["p", 0];
const brDOM: DOMOutputSpec = ["br"];
const emDOM: DOMOutputSpec = ["em", 0];
const strongDOM: DOMOutputSpec = ["strong", 0];
const uDOM: DOMOutputSpec = ["mark", 0];
const olDOM: DOMOutputSpec = ["ol", 0];
const ulDOM: DOMOutputSpec = ["ul", 0];
const liDOM: DOMOutputSpec = ["li", 0];
const subDOM: DOMOutputSpec = ["sub", 0];
const supDOM: DOMOutputSpec = ["sup", 0];
const hDOM: DOMOutputSpec[] = [
	["h1", 0],
	["h2", 0],
	["h3", 0],
];

export const mySchema = new Schema({
	nodes: {
		// :: NodeSpec The top level document node.
		doc: {
			content: "block+",
		},

		// :: NodeSpec A plain paragraph textblock. Represented in the DOM
		// as a `<p>` element.
		paragraph: {
			attrs: {
				align: { default: "left" },
				indent: { default: 0 },
			},
			content: "inline*",
			group: "block",
			parseDOM: [
				{
					tag: "p",
					getAttrs: node => ({
						align: parseDomAlignHelper(node as HTMLParagraphElement),
						indent: parseDomIndentHelper(node as HTMLParagraphElement),
					}),
				},
			],
			toDOM(node: ProseMirrorNode) {
				let classes: string[] = [];
				classes = toDomAlignHelper(node, classes);
				classes = toDomIndentHelper(node, classes);
				if (classes.length) {
					const ret: DOMOutputSpec = ["p", { class: classes.join(" ") }, 0];
					return ret;
				} else {
					return pDOM;
				}
			},
		},

		// :: NodeSpec A heading. Represented in the DOM
		// as a `<h1>` - `<h3>` element.
		heading: {
			attrs: {
				level: { default: 1 },
				align: { default: "left" },
				indent: { default: 0 },
			},
			content: "inline*",
			defining: true,
			group: "block",
			parseDOM: [
				// TODO: We may need some more rules here
				{
					tag: "h1",
					getAttrs: node => ({
						level: 1,
						align: parseDomAlignHelper(node as HTMLHeadingElement),
						indent: parseDomIndentHelper(node as HTMLHeadingElement),
					}),
				},
				{
					tag: "h2",
					getAttrs: node => ({
						level: 2,
						align: parseDomAlignHelper(node as HTMLHeadingElement),
						indent: parseDomIndentHelper(node as HTMLHeadingElement),
					}),
				},
				{
					tag: "h3",
					getAttrs: node => ({
						level: 3,
						align: parseDomAlignHelper(node as HTMLHeadingElement),
						indent: parseDomIndentHelper(node as HTMLHeadingElement),
					}),
				},
				{
					tag: "big",
					getAttrs: node => ({
						level: 3,
						align: parseDomAlignHelper(node as HTMLElement),
						indent: parseDomIndentHelper(node as HTMLElement),
					}),
				},
			],
			toDOM(node: ProseMirrorNode) {
				let classes: string[] = [];
				classes = toDomAlignHelper(node, classes);
				classes = toDomIndentHelper(node, classes);

				const hDom = hDOM[node.attrs.level - 1] as readonly [string, ...any[]];
				const hElem = hDom[0];
				if (classes.length) {
					const ret: DOMOutputSpec = [hElem, { class: classes.join(" ") }, 0];
					return ret;
				} else {
					return hDom;
				}
			},
		},

		// :: NodeSpec The text node.
		text: {
			group: "inline",
		},

		// :: NodeSpec A hard line break, represented in the DOM as `<br>`.
		hard_break: {
			inline: true,
			group: "inline",
			selectable: false,
			parseDOM: [{ tag: "br" }],
			toDOM() {
				return brDOM;
			},
		},

		ordered_list: {
			content: "list_item+",
			group: "block list",
			parseDOM: [{ tag: "ol" }],
			toDOM() {
				return olDOM;
			},
		},

		unordered_list: {
			content: "list_item+",
			group: "block list",
			parseDOM: [{ tag: "ul" }],
			toDOM() {
				return ulDOM;
			},
		},

		list_item: {
			content: "paragraph+ list*",
			defining: true,
			parseDOM: [{ tag: "li" }],
			toDOM() {
				return liDOM;
			},
		},
	},

	marks: {
		// :: MarkSpec An emphasis mark. Rendered as an `<em>` element.
		// Has parse rules that also match `<i>` and `font-style: italic`.
		em: {
			parseDOM: [{ tag: "i" }, { tag: "em" }, { style: "font-style=italic" }],
			toDOM() {
				return emDOM;
			},
		},

		// :: MarkSpec A strong mark. Rendered as `<strong>`, parse rules
		// also match `<b>` and `font-weight: bold`.
		strong: {
			parseDOM: [
				{ tag: "strong" },
				// This works around a Google Docs misbehavior where
				// pasted content will be inexplicably wrapped in `<b>`
				// tags with a font-weight normal.
				{
					tag: "b",
					getAttrs: node => (node as HTMLElement).style.fontWeight !== "normal" && null,
				},
				{
					style: "font-weight",
					getAttrs: value => /^(bold(er)?|[5-9]\d{2,})$/.test(value as string) && null,
				},
			],
			toDOM() {
				return strongDOM;
			},
		},

		u: {
			parseDOM: [{ tag: "mark" }, { tag: "u" }, { style: "text-decoration=underline" }],
			toDOM() {
				return uDOM;
			},
		},

		sub: {
			parseDOM: [{ tag: "sub" }, { style: "vertical-align=sub" }],
			toDOM() {
				return subDOM;
			},
		},

		sup: {
			parseDOM: [{ tag: "sup" }, { style: "vertical-align=super" }],
			toDOM() {
				return supDOM;
			},
		},
	},
});

function parseDomAlignHelper(node: HTMLElement) {
	let align: string | undefined;
	switch (node.style.textAlign) {
		case "left":
		case "center":
		case "right":
		case "justify":
			align = node.style.textAlign;
			break;
		case "justify-all":
			align = "justify";
		default:
			break;
	}

	if (node.classList.contains("align-left")) {
		align = "left";
	}
	if (node.classList.contains("align-center")) {
		align = "center";
	}
	if (node.classList.contains("align-right")) {
		align = "right";
	}
	if (node.classList.contains("align-justify")) {
		align = "justify";
	}
	return align;
}

function parseDomIndentHelper(node: HTMLElement) {
	let indent: number | undefined;
	for (let i = 0; i <= 12; i++) {
		if (node.classList.contains("indent-" + i)) {
			indent = i;
		}
	}
	return indent;
}

function toDomAlignHelper(node: ProseMirrorNode, classes: string[]): string[] {
	if (node.attrs.align !== "left") {
		classes.push("align-" + node.attrs.align);
	}
	return classes;
}

function toDomIndentHelper(node: ProseMirrorNode, classes: string[]): string[] {
	if (node.attrs.indent !== 0) {
		classes.push("indent-" + node.attrs.indent);
	}
	return classes;
}

export type NodeKeys = typeof mySchema extends Schema<infer N, any> ? N : never;
export type MarkKeys = typeof mySchema extends Schema<any, infer M> ? M : never;

const editorProps = {
	schema: mySchema,
	plugins: [history(), DefaultKeymap],
};
export const initialEditorState = EditorState.create(editorProps);
const serializer = DOMSerializer.fromSchema(mySchema);
const parser = DOMParser.fromSchema(mySchema);

export function createEditorStateFromDocument(doc: Node) {
	return EditorState.create({
		...editorProps,
		doc,
	});
}

export function toHtml(state: EditorState) {
	const div = document.createElement("div");
	div.appendChild(serializer.serializeFragment(state.doc.content));
	return div.innerHTML;
}
export function fromHtml(html: string): EditorState {
	const div = document.createElement("div");
	div.innerHTML = html;
	const doc = parser.parse(div, { preserveWhitespace: true });
	return createEditorStateFromDocument(doc);
}
